import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useClientAvailableContactsMutation,
  useDeleteContactMutation,
  useGetClientContactByIdMutation,
} from "../../../../api/services/client/clientApis";

import { isString } from "lodash";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  WarningConfirmationModal,
} from "../../../../components";
import {
  getParsedBEErrors,
  getParsedErrorMessage,
  hasKey,
  isEmpty,
  isNotEmpty,
} from "../../../../helpers/utils";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import { createClientSelector } from "../../../../redux/slices/client/createClientSelector";

import { default as AddIconContact } from "@mui/icons-material/AddCircleOutlineRounded";
import { useFormContext } from "react-hook-form";
import { ErrorModel } from "../../../../api/services/client/models";
import { ButtonWithIcon } from "../../../../components/buttonWithIcon/ButtonWithIcon";
import {
  BE_ERROR_CODES,
  MODULE_PERMISSIONS,
  PAGE_IDS,
} from "../../../../helpers/constants";
import { usePermissions } from "../../../../hooks/usePermissions";
import { LabelWithIconText } from "../../../../styles/common.style";
import {
  getCurrentClientContactList,
  getParentClientContactList,
  isContactBelongCurrentClient,
} from "../../helper";
import { ClientContact } from "../../modelsAndSchemas/Models";
import { createClientSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { AddNewContact } from "../addNewContact/AddNewContact";
import { ClientContactResult } from "../clientContactResult/ClientContactResult";
import { ViewContact } from "../viewContact/ViewContact";
import { ClientContactMainWrapper } from "./ClientContactMain.style";

export const pageId = PAGE_IDS.CREATE_CLIENT_CONTACT_RESULT;
export interface ClientContactMainProps {
  parentAccordionStatus: boolean;
  onAccordionChange: (expanded: boolean) => void;
  getClientContactData?: (clientContactsData: ClientContact[]) => void;
  isAddButtonVisible?: boolean;
}

export const ClientContactMain = forwardRef(
  (props: ClientContactMainProps, ref) => {
    const {
      parentAccordionStatus,
      onAccordionChange,
      getClientContactData,
      isAddButtonVisible = true,
    } = props;
    const { fetchLabel, commonLabels, fetchComponentTitle, fetchRichTexts } =
      useSiteCore();

    const { getValues } = useFormContext();
    const { billingCountry } = getValues();
    const [contactListAccordionStatus, setContactListAccordionStatus] =
      useState(parentAccordionStatus);
    const [showAddNewContactModal, setShowAddNewContactModal] = useState(false);
    const [showViewContactModal, setShowViewContactModal] = useState(false);
    const isEditContactRef = useRef(false);
    const isViewContactRef = useRef(false);
    const deleteIndexRef = useRef<string>();
    const [clientContactsData, setClientContactsData] = useState<
      ClientContact[]
    >([]);

    const dispatch = useDispatch();
    const { id: clientId } = useSelector(createClientSelector);
    const [showContactDeleteModal, setShowContactDeleteModal] = useState(false);
    const [showRetainOneContactErrorModal, setShowRetainOneContactErrorModal] =
      useState(false);
    const [
      showDeleteErrorConfirmationModal,
      setShowDeleteErrorConfirmationModal,
    ] = useState(false);
    const [
      isEditContactAttachedToCurrentClient,
      setEditContactAttachedToCurrentClient,
    ] = useState(false);
    const [warningErrorMessage, setWarningErrorMessage] = useState("");

    const { hasModulePermission } = usePermissions();

    const { contactsTitle, contactsMeetOneContactRequirements } =
      createClientSiteCoreKeys;

    const getRichLabel = (key: string) => {
      return fetchRichTexts(contactsTitle, key);
    };

    const {
      mutateAsync: getClientAvailableContacts,
      isPending: isAvailableContactsPending,
      isSuccess: isAvailableContactsSuccess,
      data: availableContacts,
    } = useClientAvailableContactsMutation();

    const {
      mutateAsync: getClientContactById,
      isPending: isGetClientContactPending,
      isError: isGetClientContactError,
      data: clientContactData,
      isSuccess: isGetClientContactSuccess,
      error: getClientContactApiError,
    } = useGetClientContactByIdMutation();

    const {
      mutateAsync: deleteContact,
      isPending: isDeleteContactPending,
      isSuccess: isDeleteContactSuccess,
      isError: isDeleteContactError,
      error: deleteContactError,
    } = useDeleteContactMutation();

    useEffect(() => {
      setContactListAccordionStatus(parentAccordionStatus);
    }, [parentAccordionStatus]);

    useEffect(() => {
      if (isDeleteContactSuccess) {
        dispatch(
          toggleToast({
            open: true,
            message: fetchLabel(
              createClientSiteCoreKeys.contactsTitle,
              createClientSiteCoreKeys.contactDeleted,
            ),
          }),
        );
        getAvailableContactsApi();
      }
    }, [isDeleteContactSuccess]);
    useEffect(() => {
      dispatch(
        toggleLoader({
          isVisible: isDeleteContactPending,
        }),
      );
    }, [isDeleteContactPending]);

    const showErrorsFromBE = (error: ErrorModel) => {
      switch (error.errorCode) {
        case BE_ERROR_CODES.CONTACT_USED_IN_BILLING:
          setWarningErrorMessage(
            fetchLabel(
              createClientSiteCoreKeys.contactsTitle,
              createClientSiteCoreKeys.deletingBillingOrHRContactMessage,
            ),
          );
          setShowDeleteErrorConfirmationModal(true);
          break;

        case BE_ERROR_CODES.CONTACT_ALREADY_MAPPED_TO_PROGRAM_PROJECT:
          setWarningErrorMessage(
            fetchLabel(
              createClientSiteCoreKeys.contactsTitle,
              createClientSiteCoreKeys.contactTypeShouldNotBeDeletedMessage,
            ),
          );
          setShowDeleteErrorConfirmationModal(true);
          break;
      }
    };

    useEffect(() => {
      if (isDeleteContactError && deleteContactError) {
        const error = getParsedBEErrors(deleteContactError)[0];
        if (isNotEmpty(error) && hasKey(error, "errorCode")) {
          showErrorsFromBE(error);
        } else {
          dispatch(
            toggleToast({
              open: true,
              message: getParsedErrorMessage(deleteContactError),
            }),
          );
        }
      }
    }, [isDeleteContactError, deleteContactError]);

    useImperativeHandle(ref, () => ({
      getAvailableContactsApi,
      handleAddContactClick,
    }));

    const filterContactBasedOnPermission = (contacts: ClientContact[]) => {
      return hasModulePermission(MODULE_PERMISSIONS.VIEW_CLIENT_PARENT_CONTACTS)
        ? contacts
        : contacts.filter((contact) => contact.isAttachedToCurrentClient);
    };

    useEffect(() => {
      if (isAvailableContactsSuccess) {
        const filteredContacts = filterContactBasedOnPermission(
          availableContacts?.data || [],
        );
        setClientContactsData(filteredContacts);
        getClientContactData?.(filteredContacts);
        if (
          !parentAccordionStatus &&
          isNotEmpty(availableContacts?.data) &&
          isAddButtonVisible
        ) {
          onAccordionChange(true);
        }
      }
    }, [isAvailableContactsSuccess]);

    const handleDeleteContactClick = () => {
      const contactId = deleteIndexRef.current ?? "";
      if (isNotEmpty(contactId)) {
        setShowContactDeleteModal(false);
        deleteContact({ contactId, clientId });
      }
    };

    const deleteContactAction = (row: ClientContact) => {
      const anyClientContactFromParent = getParentClientContactList(
        clientContactsData,
      ).find((contact) => isContactBelongCurrentClient(contact));
      if (!showContactDeleteModal) {
        deleteIndexRef.current = row.clientContactId;
        if (
          getCurrentClientContactList(clientContactsData).length === 1 &&
          isEmpty(anyClientContactFromParent)
        ) {
          setShowRetainOneContactErrorModal(true);
        } else {
          setShowContactDeleteModal(true);
        }
      }
    };

    const handleModalClose = () => {
      if (showDeleteErrorConfirmationModal) {
        setShowDeleteErrorConfirmationModal(false);
      }
      if (showContactDeleteModal) {
        setShowContactDeleteModal(false);
      }
    };

    useEffect(() => {
      dispatch(
        toggleLoader({
          isAvailableContactsPending: {
            isVisible: isAvailableContactsPending,
          },
        }),
      );
      return () => {
        dispatch(
          toggleLoader({ isAvailableContactsPending: { isVisible: false } }),
        );
      };
    }, [isAvailableContactsPending]);

    useEffect(() => {
      dispatch(
        toggleLoader({
          isVisible: isGetClientContactPending,
        }),
      );
    }, [isGetClientContactPending]);

    useEffect(() => {
      if (isGetClientContactError && getClientContactApiError) {
        dispatch(
          toggleToast({
            open: true,
            message: getParsedErrorMessage(getClientContactApiError),
          }),
        );
      }
    }, [isGetClientContactError, getClientContactApiError]);

    useEffect(() => {
      if (isEmpty(clientId)) return;
      getAvailableContactsApi();
    }, [clientId]);

    const getAvailableContactsApi = () => {
      getClientAvailableContacts(clientId);
    };

    const handleContactAdded = (contactId?: string) => {
      getAvailableContactsApi();
      if (contactId && isNotEmpty(contactId)) {
        handleViewContact(contactId);
      }
    };

    const getContactById = (contactId: string) => {
      getClientContactById({
        clientId: clientId,
        contactId: contactId,
      });
    };

    const handleViewContact = (contact: ClientContact | string) => {
      isViewContactRef.current = true;
      getContactById(isString(contact) ? contact : contact.clientContactId);
    };

    const handleEditContact = (contact: ClientContact) => {
      isViewContactRef.current = false;
      getContactById(contact.clientContactId);
      setEditContactAttachedToCurrentClient(contact.isAttachedToCurrentClient);
    };

    const handleEditContactFromViewContact = (contactId: string) => {
      isViewContactRef.current = false;
      isEditContactRef.current = true;
      setShowViewContactModal(false);
      getContactById(contactId);
    };

    useEffect(() => {
      if (isGetClientContactSuccess) {
        if (isViewContactRef.current) {
          setShowViewContactModal(true);
        } else {
          isEditContactRef.current = true;
          setShowAddNewContactModal(true);
        }
      }
    }, [isGetClientContactSuccess]);

    const handleAddContactModalClose = () => {
      if (showAddNewContactModal) {
        isEditContactRef.current = false;
        setShowAddNewContactModal(false);
      }
    };

    const handleAddContactClick = () => {
      if (!showAddNewContactModal) {
        isEditContactRef.current = false;
        setShowAddNewContactModal(true);
      }
    };

    const handleCreateClientContactListChange = (
      _event: React.SyntheticEvent,
      expanded: boolean,
    ) => {
      setContactListAccordionStatus(expanded);
      onAccordionChange(expanded);
    };

    const handleRetainOneContactModalClose = () => {
      if (showRetainOneContactErrorModal) {
        setShowRetainOneContactErrorModal(false);
      }
    };

    return (
      <ClientContactMainWrapper>
        <AccordionHeader
          title={
            <AccordionHeaderText
              label={fetchComponentTitle(
                createClientSiteCoreKeys.contactsTitle,
              )}
            />
          }
          onChange={handleCreateClientContactListChange}
          isExpanded={contactListAccordionStatus}
        >
          {isAddButtonVisible &&
            hasModulePermission(MODULE_PERMISSIONS.ADD_CLIENT_CONTACTS) && (
              <MuiBox className="semi-header">
                <ButtonWithIcon
                  buttonText={fetchComponentTitle(
                    createClientSiteCoreKeys.addContactTitle,
                  )}
                  onActionClick={handleAddContactClick}
                />
              </MuiBox>
            )}

          <ClientContactResult
            rows={clientContactsData || []}
            onClientEdit={handleEditContact}
            onClientView={handleViewContact}
            onDelete={deleteContactAction}
            isEditable={
              isAddButtonVisible &&
              (hasModulePermission(MODULE_PERMISSIONS.DELETE_CLIENT_CONTACTS) ||
                hasModulePermission(MODULE_PERMISSIONS.EDIT_CLIENT_CONTACTS))
            }
          />
        </AccordionHeader>
        {showAddNewContactModal && (
          <AddNewContact
            open={showAddNewContactModal}
            onClose={handleAddContactModalClose}
            onContactAdded={handleContactAdded}
            isEdit={isEditContactRef.current}
            contactData={clientContactData?.data}
            billingCountry={billingCountry}
            isAttachedToCurrentClient={isEditContactAttachedToCurrentClient}
          />
        )}
        {showViewContactModal && (
          <ViewContact
            open={showViewContactModal}
            onClose={() => setShowViewContactModal(false)}
            contactData={clientContactData?.data}
            onClientEdit={handleEditContactFromViewContact}
            isAddButtonVisible={isAddButtonVisible}
          />
        )}
        {showContactDeleteModal && (
          <WarningConfirmationModal
            show={showContactDeleteModal}
            modalTitle={commonLabels.confirmation}
            onClose={handleModalClose}
            onPrimaryButtonClick={handleDeleteContactClick}
            labelContent={fetchLabel(
              createClientSiteCoreKeys.contactsTitle,
              createClientSiteCoreKeys.deleteContactActionText,
            )}
          />
        )}

        {showDeleteErrorConfirmationModal && (
          <WarningConfirmationModal
            show={showDeleteErrorConfirmationModal}
            onClose={handleModalClose}
            onPrimaryButtonClick={handleModalClose}
            showSecondaryButton={false}
            primaryButtonTitle={commonLabels.ok}
            labelContent={warningErrorMessage}
          />
        )}

        {showRetainOneContactErrorModal && (
          <WarningConfirmationModal
            labelContent={getRichLabel(contactsMeetOneContactRequirements)}
            isLabelHTMLContent={true}
            show={showRetainOneContactErrorModal}
            onClose={handleRetainOneContactModalClose}
            onPrimaryButtonClick={handleRetainOneContactModalClose}
            primaryButtonTitle={commonLabels.ok}
            showSecondaryButton={false}
          />
        )}
      </ClientContactMainWrapper>
    );
  },
);
